import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Badge, Button, Input, Alert, AlertDescription, Spinner } from '@klaro7/ui';
import { Permission, UserRole, UserStatus } from '@klaro7/shared-types';

import {
  useAdminUserById,
  useForceUserPassword,
  useUpdateAdminUser,
  useDeleteAdminUser,
  useGrantGiftCredit,
  useUserWalletTransactions,
} from '@/features/users/hooks/useAdminUsers';
import type { AdminUser } from '@/features/users/services/user-admin.service';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { usePermissions } from '@/features/rbac/usePermissions';
import { resolveUploadUrl } from '@/lib/uploads';
import { useBackTo } from '@/lib/listUrlState';
import { txMeta } from '@/features/users/lib/walletTxLabels';
import { AdminCallHistoryCard } from '@/features/calls/components/AdminCallHistoryCard';
import { AdminUserFavoritesCard } from '@/features/favorites/components/AdminFavoritesCards';
import { FraudReviewCard } from '@/features/fraud/components/FraudReviewCard';
import { UserActivityRecentCard } from '@/features/user-activity/components/UserActivityRecentCard';
import { PhoneField } from '@/components/PhoneField';
import { EmailField } from '@/components/EmailField';

const roleLabels: Record<
  string,
  { label: string; variant: 'primary' | 'secondary' | 'warning' | 'info' }
> = {
  super_admin: { label: 'Super Admin', variant: 'primary' },
  admin: { label: 'Admin', variant: 'primary' },
  professional: { label: 'Professionista', variant: 'secondary' },
  user: { label: 'Utente', variant: 'info' },
};

const statusLabels: Record<
  string,
  { label: string; variant: 'success' | 'warning' | 'error' | 'default' }
> = {
  active: { label: 'Attivo', variant: 'success' },
  pending: { label: 'In Attesa', variant: 'warning' },
  waiting_questionnaire: { label: 'Questionario in corso', variant: 'warning' },
  suspended: { label: 'Sospeso', variant: 'error' },
  inactive: { label: 'Inattivo', variant: 'default' },
};

const euroFmt = new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' });
function formatEuro(amount: number): string {
  return euroFmt.format(amount);
}

function Field({ label, value }: { label: string; value?: string | null }) {
  return (
    <div>
      <dt className="text-xs text-neutral-400">{label}</dt>
      <dd className="mt-0.5 text-sm text-neutral-900">
        {value ?? <span className="text-neutral-300">—</span>}
      </dd>
    </div>
  );
}

/** Super-admin only: force a new password for this user. */
function ForcePasswordCard({ userId, userName }: { userId: string; userName: string }) {
  const forcePassword = useForceUserPassword();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [alert, setAlert] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setAlert(null);
    if (password.length < 8) {
      setAlert({ type: 'error', message: 'La password deve avere almeno 8 caratteri' });
      return;
    }
    if (password !== confirm) {
      setAlert({ type: 'error', message: 'Le password non coincidono' });
      return;
    }
    try {
      await forcePassword.mutateAsync({ id: userId, password });
      setAlert({ type: 'success', message: 'Password aggiornata con successo' });
      setPassword('');
      setConfirm('');
    } catch {
      setAlert({ type: 'error', message: 'Errore durante l’aggiornamento della password' });
    }
  };

  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-5">
      <h3 className="mb-1 text-sm font-semibold uppercase tracking-wider text-amber-700">
        Forza password
      </h3>
      <p className="mb-4 text-xs text-amber-700/80">
        Solo Super Admin. Imposta una nuova password per{' '}
        <span className="font-medium">{userName}</span>. L’utente potrà accedere subito con la
        password scelta.
      </p>

      {alert && (
        <Alert variant={alert.type === 'success' ? 'success' : 'error'} className="mb-4">
          <AlertDescription>{alert.message}</AlertDescription>
        </Alert>
      )}

      <form onSubmit={handleSubmit} className="space-y-3">
        <Input
          type={show ? 'text' : 'password'}
          placeholder="Nuova password (min. 8 caratteri)"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="new-password"
        />
        <Input
          type={show ? 'text' : 'password'}
          placeholder="Conferma password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          autoComplete="new-password"
        />
        <label className="flex items-center gap-2 text-xs text-neutral-500">
          <input
            type="checkbox"
            checked={show}
            onChange={(e) => setShow(e.target.checked)}
            className="h-3.5 w-3.5 accent-brand-teal"
          />
          Mostra password
        </label>
        <Button type="submit" disabled={forcePassword.isPending || !password || !confirm}>
          {forcePassword.isPending ? 'Salvataggio…' : 'Imposta password'}
        </Button>
      </form>
    </div>
  );
}

/** Super-admin only: grant a no-expiry gift credit to the user's bonus wallet. */
function GiftCreditCard({ userId }: { userId: string }) {
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const grant = useGrantGiftCredit(userId);

  const amountNum = Number(amount);
  const canSubmit = amountNum > 0 && reason.trim().length > 0 && !grant.isPending;

  return (
    <div className="rounded-xl border border-neutral-200 bg-white p-5">
      <h3 className="mb-1 text-sm font-semibold uppercase tracking-wider text-neutral-400">
        Credito omaggio
      </h3>
      <p className="mb-4 text-sm text-neutral-500">
        Accredita credito bonus gratuito (senza scadenza) al borsellino dell'utente.
      </p>
      <div className="grid gap-4 sm:grid-cols-2">
        <Input
          label="Importo (€)"
          type="number"
          min={0}
          step={0.01}
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
        />
        <Input
          label="Motivo (obbligatorio)"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
      </div>
      <div className="mt-4 flex justify-end">
        <Button
          onClick={() =>
            grant.mutate(
              { amount: amountNum, reason: reason.trim() },
              {
                onSuccess: () => {
                  setAmount('');
                  setReason('');
                },
              }
            )
          }
          loading={grant.isPending}
          disabled={!canSubmit}
        >
          Accredita
        </Button>
      </div>
    </div>
  );
}

function UserWalletHistoryCard({ userId }: { userId: string }) {
  const [page, setPage] = useState(1);
  const { data, isLoading, isError } = useUserWalletTransactions(userId, page);
  const items = data?.items ?? [];
  const pg = data?.pagination;

  return (
    <div className="rounded-xl border border-neutral-200 bg-white">
      <div className="border-b border-neutral-100 px-5 py-3.5">
        <h3 className="text-sm font-semibold text-neutral-700">Storico movimenti</h3>
        <p className="text-xs text-neutral-500">Tutte le movimentazioni del borsellino</p>
      </div>
      <div className="p-5">
        {isLoading ? (
          <div className="flex justify-center py-6">
            <Spinner />
          </div>
        ) : isError ? (
          <p className="text-sm text-red-600">Errore nel caricamento dei movimenti.</p>
        ) : items.length === 0 ? (
          <p className="text-sm text-neutral-400">Nessun movimento.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-neutral-100 text-sm">
              <thead className="text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
                <tr>
                  <th className="px-2 py-2">Data</th>
                  <th className="px-2 py-2">Tipo</th>
                  <th className="px-2 py-2 text-right">Importo</th>
                  <th
                    className="px-2 py-2 text-right"
                    title="Saldo totale (bonus + reale) dopo la transazione"
                  >
                    Saldo totale
                  </th>
                  <th className="px-2 py-2">Descrizione</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {items.map((t) => {
                  const meta = txMeta(t.type, t.amount);
                  return (
                    <tr key={t._id}>
                      <td className="whitespace-nowrap px-2 py-2 text-neutral-500">
                        {new Date(t.createdAt).toLocaleString('it-IT', {
                          day: '2-digit',
                          month: '2-digit',
                          year: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </td>
                      <td className="px-2 py-2 text-neutral-700">{meta.label}</td>
                      <td
                        className={`whitespace-nowrap px-2 py-2 text-right font-medium ${meta.credit ? 'text-emerald-600' : 'text-red-600'}`}
                      >
                        {meta.credit ? '+' : '-'}
                        {formatEuro(Math.abs(t.amount))}
                      </td>
                      <td className="whitespace-nowrap px-2 py-2 text-right text-neutral-500">
                        {formatEuro(t.balanceAfter)}
                      </td>
                      <td className="px-2 py-2 text-neutral-500">{t.description}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {pg && pg.totalPages > 1 && (
          <div className="mt-3 flex items-center justify-between">
            <span className="text-xs text-neutral-500">
              {pg.total} movimenti · pag. {pg.page}/{pg.totalPages}
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={!pg.hasPrevPage}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Precedente
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={!pg.hasNextPage}
                onClick={() => setPage((p) => p + 1)}
              >
                Successiva
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const ROLE_OPTIONS: { value: UserRole; label: string }[] = [
  { value: UserRole.USER, label: 'Utente' },
  { value: UserRole.PROFESSIONAL, label: 'Professionista' },
  { value: UserRole.ADMIN, label: 'Admin' },
  { value: UserRole.SUPER_ADMIN, label: 'Super Admin' },
];

const STATUS_OPTIONS: { value: UserStatus; label: string }[] = [
  { value: UserStatus.ACTIVE, label: 'Attivo' },
  { value: UserStatus.PENDING, label: 'In Attesa' },
  { value: UserStatus.WAITING_QUESTIONNAIRE, label: 'Questionario in corso' },
  { value: UserStatus.SUSPENDED, label: 'Sospeso' },
  { value: UserStatus.INACTIVE, label: 'Inattivo' },
];

/**
 * Admin management card: change role/status, quick suspend/reactivate, and delete.
 * Destructive actions (suspend, delete) require a confirmation before firing.
 */
function ManageUserCard({ user, userName }: { user: AdminUser; userName: string }) {
  const navigate = useNavigate();
  const backTo = useBackTo('/dashboard/admin/users');
  const updateUser = useUpdateAdminUser();
  const deleteUser = useDeleteAdminUser();

  const [role, setRole] = useState<UserRole>(user.role as UserRole);
  const [status, setStatus] = useState<UserStatus>(user.status as UserStatus);

  // Keep the local form in sync if the underlying user data refreshes.
  useEffect(() => {
    setRole(user.role as UserRole);
    setStatus(user.status as UserStatus);
  }, [user.role, user.status]);

  const isSuspended = status === UserStatus.SUSPENDED || status === UserStatus.INACTIVE;
  const dirty = role !== user.role || status !== user.status;
  const busy = updateUser.isPending || deleteUser.isPending;

  const handleSave = () => {
    if (status === UserStatus.SUSPENDED && user.status !== UserStatus.SUSPENDED) {
      const ok = window.confirm(
        `Sospendere ${userName}?\n\nL'utente non potrà più accedere fino alla riattivazione.`
      );
      if (!ok) return;
    }
    updateUser.mutate({ id: user.id, payload: { role, status } });
  };

  const handleToggleStatus = () => {
    const next = isSuspended ? UserStatus.ACTIVE : UserStatus.SUSPENDED;
    if (next === UserStatus.SUSPENDED) {
      const ok = window.confirm(
        `Sospendere ${userName}?\n\nL'utente non potrà più accedere fino alla riattivazione.`
      );
      if (!ok) return;
    }
    setStatus(next);
    updateUser.mutate({ id: user.id, payload: { status: next } });
  };

  const handleDelete = () => {
    const ok = window.confirm(
      `Eliminare definitivamente ${userName}?\n\nQuesta azione è irreversibile e rimuove tutti i dati associati.`
    );
    if (!ok) return;
    deleteUser.mutate(user.id, {
      onSuccess: () => navigate(backTo),
    });
  };

  return (
    <div className="rounded-xl border border-neutral-200 bg-white p-5">
      <h3 className="mb-4 text-sm font-semibold uppercase tracking-wider text-neutral-400">
        Gestione utente
      </h3>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-xs text-neutral-400">Ruolo</label>
          <select
            value={role}
            onChange={(e) => setRole(e.target.value as UserRole)}
            disabled={busy}
            className="h-9 w-full rounded-lg border border-neutral-200 bg-white px-3 text-sm text-neutral-900 focus:border-brand-teal/50 focus:outline-none disabled:opacity-50"
          >
            {ROLE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="mb-1 block text-xs text-neutral-400">Stato</label>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as UserStatus)}
            disabled={busy}
            className="h-9 w-full rounded-lg border border-neutral-200 bg-white px-3 text-sm text-neutral-900 focus:border-brand-teal/50 focus:outline-none disabled:opacity-50"
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <Button onClick={handleSave} disabled={!dirty || busy}>
          {updateUser.isPending ? 'Salvataggio…' : 'Salva modifiche'}
        </Button>
        <Button variant="outline" onClick={handleToggleStatus} disabled={busy}>
          {isSuspended ? 'Riattiva' : 'Sospendi'}
        </Button>
        <div className="ml-auto">
          <Button variant="danger" onClick={handleDelete} disabled={busy}>
            {deleteUser.isPending ? 'Eliminazione…' : 'Elimina utente'}
          </Button>
        </div>
      </div>

      {user.statusHistory && user.statusHistory.length > 0 && (
        <ul className="mt-3 space-y-0.5 text-[10px] text-neutral-400">
          {user.statusHistory
            .slice()
            .reverse()
            .map((h, i) => (
              <li key={i}>
                → {statusLabels[h.status]?.label ?? h.status} ·{' '}
                {new Date(h.at).toLocaleString('it-IT')} · {h.byAdminName}
              </li>
            ))}
        </ul>
      )}
    </div>
  );
}

export function AdminUserDetailPage() {
  const { id } = useParams<{ id: string }>();
  const backTo = useBackTo('/dashboard/admin/users');
  const { hasRole } = useAuth();
  const { can } = usePermissions();
  const { data: user, isLoading } = useAdminUserById(id!);

  if (isLoading) {
    return (
      <div className="flex justify-center py-24">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="py-24 text-center">
        <p className="text-neutral-400">Utente non trovato</p>
        <Link to={backTo} className="mt-3 inline-block text-sm text-brand-teal underline">
          Torna alla lista
        </Link>
      </div>
    );
  }

  const fullName = `${user.profile?.firstName ?? ''} ${user.profile?.lastName ?? ''}`.trim();
  const role = roleLabels[user.role] ?? { label: user.role, variant: 'info' as const };
  const status = statusLabels[user.status] ?? { label: user.status, variant: 'default' as const };
  const avatarUrl = resolveUploadUrl(user.profile?.avatar);

  return (
    <div className="space-y-6">
      {/* Back + header */}
      <div className="flex items-center gap-3">
        <Link
          to={backTo}
          className="rounded-lg p-1.5 text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-600"
        >
          <svg
            className="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
        </Link>
        <h1 className="text-2xl font-bold text-neutral-900">Profilo Utente</h1>
      </div>

      {/* Identity card */}
      <div className="flex flex-col items-center gap-5 rounded-xl border border-neutral-200 bg-white p-6 sm:flex-row sm:items-start">
        {/* Avatar */}
        <div className="relative h-24 w-24 shrink-0">
          <div className="flex h-24 w-24 items-center justify-center rounded-full bg-neutral-100">
            <img
              src="/images/logo-navy.png"
              alt=""
              className="h-14 w-14 object-contain opacity-60"
            />
          </div>
          {avatarUrl && (
            <img
              src={avatarUrl}
              alt={fullName}
              className="absolute inset-0 h-24 w-24 rounded-full object-cover"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
          )}
        </div>

        {/* Info */}
        <div className="min-w-0 flex-1 text-center sm:text-left">
          <h2 className="text-xl font-bold text-neutral-900">{fullName || 'Utente'}</h2>
          <p className="text-sm text-neutral-500">{user.email}</p>
          <div className="mt-3 flex flex-wrap items-center justify-center gap-2 sm:justify-start">
            <Badge variant={role.variant}>{role.label}</Badge>
            <Badge variant={status.variant}>{status.label}</Badge>
          </div>
        </div>
        {/* Timeline attività + scrivi all'utente — super-admin only (come gli endpoint) */}
        {hasRole(UserRole.SUPER_ADMIN) && (
          <div className="flex shrink-0 flex-col gap-2">
            <Link
              to={`/dashboard/admin/users/${user.id}/timeline`}
              className="inline-flex shrink-0 items-center gap-2 rounded-lg border border-neutral-200 bg-white px-4 py-2 text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-50"
            >
              <svg
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.8}
                aria-hidden
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              Storico attività
            </Link>
            {user.role === UserRole.USER && (
              <Link
                to={`/dashboard/admin/support-users?userId=${user.id}`}
                className="inline-flex items-center gap-2 rounded-lg border border-neutral-200 bg-white px-4 py-2 text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-50"
              >
                <svg
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={1.8}
                  aria-hidden
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M8 10h8M8 14h5m-9 6l2.4-3.2A2 2 0 017.999 16H18a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v14z"
                  />
                </svg>
                Scrivi all&apos;utente
              </Link>
            )}
          </div>
        )}
      </div>

      {/* Details */}
      <div className="rounded-xl border border-neutral-200 bg-white p-5">
        <h3 className="mb-4 text-sm font-semibold uppercase tracking-wider text-neutral-400">
          Informazioni personali
        </h3>
        <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Nome" value={user.profile?.firstName} />
          <Field label="Cognome" value={user.profile?.lastName} />
          <EmailField
            email={user.email}
            verified={user.profile?.emailVerified}
            verifiedAt={user.profile?.emailVerifiedAt}
          />
          <PhoneField
            phone={user.profile?.phone}
            verified={user.profile?.phoneVerified}
            verifiedAt={user.profile?.phoneVerifiedAt}
          />
          {user.profile?.pendingPhone && (
            <div>
              <dt className="text-xs text-neutral-400">Nuovo numero (in attesa di verifica)</dt>
              <dd className="text-sm font-medium text-amber-700">{user.profile.pendingPhone}</dd>
            </div>
          )}
          <Field label="Ruolo" value={role.label} />
          <Field label="Stato" value={status.label} />
        </dl>
        {user.profile?.bio && (
          <div className="mt-4">
            <dt className="text-xs text-neutral-400">Bio</dt>
            <dd className="mt-0.5 text-sm text-neutral-700">{user.profile.bio}</dd>
          </div>
        )}
      </div>

      {/* Segnalazione antifrode multi-account — solo se l'utente ha una
          storia antifrode; l'endpoint dei dettagli è super-admin only */}
      {user.fraudStatus && hasRole(UserRole.SUPER_ADMIN) && <FraudReviewCard userId={user.id} />}

      {/* Credito borsellino */}
      <div className="rounded-xl border border-neutral-200 bg-white p-5">
        <h3 className="mb-4 text-sm font-semibold uppercase tracking-wider text-neutral-400">
          Borsellino
        </h3>
        {user.wallet ? (
          <dl className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <dt className="text-xs text-neutral-400">Credito ricarica</dt>
              <dd className="mt-0.5 text-2xl font-bold text-brand-teal">
                {formatEuro(user.wallet.balance)}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-neutral-400">Credito bonus</dt>
              <dd className="mt-0.5 text-lg font-semibold text-neutral-700">
                {formatEuro(user.wallet.bonusBalance)}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-neutral-400">Totale disponibile</dt>
              <dd className="mt-0.5 text-lg font-semibold text-neutral-900">
                {formatEuro(user.wallet.totalBalance)}
              </dd>
            </div>
          </dl>
        ) : (
          <p className="text-sm text-neutral-400">Nessun borsellino attivo per questo utente.</p>
        )}
      </div>

      <UserWalletHistoryCard userId={user.id} />

      <AdminCallHistoryCard callerId={user.id} />

      {hasRole(UserRole.SUPER_ADMIN) && <AdminUserFavoritesCard userId={user.id} />}

      {hasRole(UserRole.SUPER_ADMIN) && <UserActivityRecentCard userId={user.id} />}

      {/* Gestione utente — change role/status, suspend, delete */}
      {(can(Permission.USERS_UPDATE) || can(Permission.USERS_MANAGE)) && (
        <ManageUserCard user={user} userName={fullName || user.email} />
      )}

      {/* Forza password — Super Admin only */}
      {hasRole(UserRole.SUPER_ADMIN) && (
        <>
          <ForcePasswordCard userId={user.id} userName={fullName || user.email} />
          <GiftCreditCard userId={user.id} />
        </>
      )}

      {/* Metadata */}
      <div className="rounded-xl border border-neutral-200 bg-neutral-50 px-5 py-3">
        <div className="flex flex-wrap gap-6 text-xs text-neutral-400">
          <span>
            ID: <code className="text-neutral-600">{user.id}</code>
          </span>
          <span>
            Registrato:{' '}
            {new Date(user.createdAt).toLocaleDateString('it-IT', {
              day: 'numeric',
              month: 'long',
              year: 'numeric',
            })}
          </span>
          <span>
            Ultimo aggiornamento:{' '}
            {new Date(user.updatedAt).toLocaleDateString('it-IT', {
              day: 'numeric',
              month: 'long',
              year: 'numeric',
            })}
          </span>
        </div>
      </div>
    </div>
  );
}
